import { Component, useCallback, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2, ZoomIn, ZoomOut } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 2, 2.5, 3];

class PdfErrorBoundary extends Component {
    state = { failed: false };
    static getDerivedStateFromError() { return { failed: true }; }
    render() {
        if (this.state.failed) {
            return (
                <div className="flex h-full items-center justify-center p-6 text-center text-sm text-slate-600" data-testid="pdf-fallback">
                    L'anteprima non è supportata da questo dispositivo o browser.<br />
                    Usa il pulsante "Scarica" per aprire il documento.
                </div>
            );
        }
        return this.props.children;
    }
}

/** Multi-page PDF viewer (PDF.js) — works on mobile where iframes show only page 1.
 *  Includes zoom controls and touch-friendly scrolling. */
const PdfViewerInner = ({ fileUrl }) => {
    const [numPages, setNumPages] = useState(null);
    const [width, setWidth] = useState(null);
    const [zoomIdx, setZoomIdx] = useState(1);
    const containerRef = useCallback((node) => {
        if (node) setWidth(node.clientWidth);
    }, []);

    const zoom = ZOOM_STEPS[zoomIdx];
    const pageWidth = width ? Math.round(width * zoom) : undefined;

    return (
        <div className="relative h-full w-full">
            <div
                ref={containerRef}
                className="h-full w-full overflow-auto"
                style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-x pan-y pinch-zoom" }}
                data-testid="pdf-viewer"
            >
                <Document
                    file={fileUrl}
                    onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                    loading={
                        <div className="flex justify-center p-10">
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    }
                    error={
                        <div className="p-6 text-center text-sm text-red-600">
                            Impossibile visualizzare il PDF. Usa "Scarica" per aprirlo.
                        </div>
                    }
                >
                    {Array.from({ length: numPages || 0 }, (_, i) => (
                        <Page
                            key={i}
                            pageNumber={i + 1}
                            width={pageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            className="mb-2 border-b border-slate-200"
                            loading={null}
                        />
                    ))}
                </Document>
                {numPages > 1 && (
                    <div className="pb-2 pt-1 text-center text-[11px] text-slate-400" data-testid="pdf-page-count">
                        {numPages} pagine
                    </div>
                )}
            </div>
            {numPages > 0 && (
                <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1 rounded-full bg-slate-900/80 px-1.5 py-1 shadow-lg backdrop-blur">
                    <button
                        type="button"
                        onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
                        disabled={zoomIdx === 0}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors hover:bg-white/15 disabled:opacity-40"
                        aria-label="Riduci zoom"
                        data-testid="pdf-zoom-out"
                    >
                        <ZoomOut className="h-4 w-4" />
                    </button>
                    <span className="min-w-[42px] text-center text-xs font-semibold text-white" data-testid="pdf-zoom-level">
                        {Math.round(zoom * 100)}%
                    </span>
                    <button
                        type="button"
                        onClick={() => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
                        disabled={zoomIdx === ZOOM_STEPS.length - 1}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors hover:bg-white/15 disabled:opacity-40"
                        aria-label="Aumenta zoom"
                        data-testid="pdf-zoom-in"
                    >
                        <ZoomIn className="h-4 w-4" />
                    </button>
                </div>
            )}
        </div>
    );
};

export const PdfViewer = (props) => (
    <PdfErrorBoundary>
        <PdfViewerInner {...props} />
    </PdfErrorBoundary>
);
